import { assert, trackWorkerExceptions } from '@fe/core';
import { Actor, ActorMessage } from './actor/actor';
import { MessageType, RequestResponseMessageMap } from './actor/actor-messages';

type WorkerConstructor = {
    new (options?: { name?: string }): Worker;
};

let WorkerFactory: WorkerConstructor | null = null;

export function setWorkerFactory(factory: WorkerConstructor) {
    WorkerFactory = factory;
}

export class Dispatcher {
    private worker: Worker;

    private readonly actor: Actor;
    private isInitialized = false;
    private initializePromise: Promise<void>;
    private resolveIsInitialized!: () => void;

    constructor() {
        assert(WorkerFactory);
        this.initializePromise = new Promise((resolve) => {
            this.resolveIsInitialized = resolve;
        });

        this.worker = new WorkerFactory({ name: 'fe-profile-worker' });
        trackWorkerExceptions(this.worker);
        this.actor = new Actor(this.worker);
        this.actor.registerMessageHandler(MessageType.initialized, async () => {
            this.isInitialized = true;
            this.resolveIsInitialized();
        });
    }

    dispatch<T extends MessageType>(message: ActorMessage<T>): Promise<RequestResponseMessageMap[T][1]> {
        if (this.isInitialized) {
            return this.actor.sendAsync(message);
        }

        // Wait for the worker to initialize
        return new Promise<RequestResponseMessageMap[T][1]>((resolve, reject) => {
            this.initializePromise.then(() => this.actor.sendAsync(message).then(resolve).catch(reject));
        });
    }

    remove() {
        this.actor.remove();
    }
}

let globalDispatcher: Dispatcher | null = null;

export function getGlobalDispatcher() {
    if (typeof WorkerGlobalScope !== 'undefined' && typeof self !== 'undefined' && self instanceof WorkerGlobalScope) {
        throw new Error('Dispatcher is only available in non-worker context');
    }
    return (globalDispatcher ??= new Dispatcher());
}

export function removeGlobalDispatcher() {
    globalDispatcher?.remove();
    globalDispatcher = null;
}
